import React, { useState } from 'react';
import { Autocomplete, TextField, InputAdornment, Box, Typography, IconButton, Tooltip } from '@mui/material';
import { StorefrontOutlined, AddCircleOutline } from '@mui/icons-material';
import { useFacilityMaintenanceEditor } from '../../../../../hooks/useFacilityMaintenanceEditor';
import { useGetActiveVendorsQuery } from '../../../../../../../services/VendorService';
import { VendorWorklistView } from '../../../../../../../services/VendorService';
import { VendorQuickAddDialog } from '../../../../shared/VendorQuickAddDialog';
import { EditorMode } from '../../../../../types/editorTypes';

interface Props {
  mode: EditorMode;
}

/**
 * FacilityMaintenanceVendorAutocomplete - Autocomplete for selecting a vendor on FM tickets.
 * Stores only vendorId; vendor details are resolved from the cached dropdown data.
 */
export const FacilityMaintenanceVendorAutocomplete: React.FC<Props> = ({ mode }) => {
  const { request, updateRequest } = useFacilityMaintenanceEditor(mode);
  const { data: vendors = [], isLoading } = useGetActiveVendorsQuery();
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  const selectedVendor = vendors.find(v => v.id === request?.vendorId) || null;

  const handleChange = (_: React.SyntheticEvent, value: VendorWorklistView | null) => {
    updateRequest({ vendorId: value?.id || '' });
  };

  const handleVendorCreated = (vendorId: string) => {
    updateRequest({ vendorId });
  };

  return (
    <>
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <Autocomplete
          sx={{ flex: 1 }}
          size="small"
          options={vendors}
          value={selectedVendor}
          onChange={handleChange}
          loading={isLoading}
          getOptionLabel={(option) => option.companyName}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {option.companyName}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  {option.primaryContactName}{option.primaryPhone ? ` • ${option.primaryPhone}` : ''}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Vendor"
              placeholder="Search vendors..."
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <StorefrontOutlined sx={{ color: '#64748b', fontSize: 18 }} />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Tooltip title="Quick add vendor">
          <IconButton
            size="small"
            onClick={() => setQuickAddOpen(true)}
            sx={{ mt: 0.5, color: '#3b82f6' }}
          >
            <AddCircleOutline fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <VendorQuickAddDialog
        open={quickAddOpen}
        onClose={() => setQuickAddOpen(false)}
        onVendorCreated={handleVendorCreated}
      />
    </>
  );
};

export default FacilityMaintenanceVendorAutocomplete;
